import React, { useEffect, useState } from 'react';

const sections = [
    { id: 'section1', title: '1. Introduction' },
    { id: 'section2', title: '2. Types of Personal Information We Collect' },
    { id: 'section3', title: '3. How We Collect Personal Information' },
    { id: 'section4', title: '4. Use of Your Personal Information' },
    { id: 'section5', title: '5. Security' },
    { id: 'section6', title: '6. Links' },
    { id: 'section7', title: '7. Requesting Access or Correcting Your Personal Information' },
    { id: 'section8', title: '8. Complaints' },
    { id: 'section9', title: '9. Contact Us' },
    { id: 'section10', title: 'PDF Download' }
];

export function PrivacyPolicy () {
    const [activeId, setActiveId] = useState(null);
    const [isMobileTOCOpen, setIsMobileTOCOpen] = useState(false);

    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.find(e => e.isIntersecting);
                if (visible) setActiveId(visible.target.id);
            },
            { threshold: 0.4 }
        );
        sections.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    return (
        <div className="privacy-policy-container" style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }}>
            <nav
                className="privacy-policy-toc"
                style={{
                    position: 'sticky',
                    top: '6rem',
                    maxHeight: 'calc(100vh - 6rem)',
                    overflowY: 'auto',
                    minWidth: '220px',
                    borderRight: '1px solid #e0e0e0',
                    paddingRight: '1rem',
                    display: isMobileTOCOpen ? 'block' : 'none',
                }}
            >
                <h2>Contents</h2>
                <ul style={{ listStyle: 'disc', paddingLeft: '1.2rem' }}>
                    {sections.map(({ id, title }) => (
                        <li key={id}>
                            <a
                                href={`#${id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    const target = document.getElementById(id);
                                    if (target) {
                                        setTimeout(() => {
                                            const yOffset = -100; // adjust to match your header height
                                            const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                            window.scrollTo({ top: y, behavior: 'smooth' });
                                        }, 0);
                                    }
                                    setIsMobileTOCOpen(false);
                                }}
                                style={{ fontWeight: activeId === id ? 'bold' : 'normal' }}
                            >
                                {title}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <div style={{ flex: 1 }}>
                <button
                    onClick={() => setIsMobileTOCOpen(!isMobileTOCOpen)}
                    className="toc-toggle"
                    style={{ marginBottom: '1rem', display: 'block', background: '#eee', border: 'none', padding: '0.5rem 1rem' }}
                >
                    {isMobileTOCOpen ? 'Hide Contents' : 'Show Contents'}
                </button>

                <h1 className="privacy-policy-title">Privacy Policy</h1>
                <p className="privacy-policy-updated">Last updated: 31 August 2020</p>

                <section id="section1">
                    <h2>1. Introduction</h2>
                    <p>This document sets out the privacy policy of Streamwisedi Pty Ltd ABN 43 641 597 373 (referred to in these terms and conditions as 'we', 'us', or 'our').</p>
                    <p>We take our privacy obligations seriously and we've created this privacy policy to explain how we store, maintain, use and disclose personal information.</p>
                    <p>By providing personal information to us, you consent to our storage, maintenance, use and disclosing of personal information in accordance with this privacy policy.</p>
                    <p>We may change this privacy policy from time to time by posting an updated copy on our website and we encourage you to check our website regularly to ensure that you are aware of our most current privacy policy.</p>
                </section>

                <section id="section2">
                    <h2>2. Types of Personal Information We Collect</h2>
                    <ul>
                        <li>name;</li>
                        <li>mailing or street address;</li>
                        <li>email address;</li>
                        <li>social media information;</li>
                        <li>telephone number and other contact details;</li>
                        <li>age;</li>
                        <li>date of birth;</li>
                        <li>images and recordings;</li>
                        <li>credit card or other payment information;</li>
                        <li>information about your business or personal circumstances;</li>
                        <li>information in connection with client surveys, questionnaires and promotions;</li>
                        <li>your device identity and type, IP address, geo-location information, page view statistics, advertising data and standard web log information;</li>
                        <li>information about third parties; and</li>
                        <li>any other information provided by you to us via our website or our online presence, or otherwise required by us or provided by you.</li>
                    </ul>
                </section>

                <section id="section3">
                    <h2>3. How We Collect Personal Information</h2>
                    <p>We may collect personal information either directly from you, or from third parties, including where you:</p>
                    <ul>
                        <li>contact us through our website;</li>
                        <li>submit any of our online sign up forms;</li>
                        <li>communicate with us via email, telephone, SMS, social applications (such as LinkedIn, Facebook or Twitter) or otherwise;</li>
                        <li>interact with our website, social applications, services, content and advertising; and</li>
                        <li>invest in our business or enquire as to a potential purchase in our business.</li>
                    </ul>
                    <p>We may also collect personal information from you when you use or access our website or our social media pages. This may be done through use of web analytics tools, 'cookies' or other similar tracking technologies that allow us to track and analyse your website usage. Cookies are small files that store information on your computer, mobile phone or other device and enable and allow the creator of the cookie to identify when you visit different websites. If you do not wish information to be stored as a cookie, you can disable cookies in your web browser.</p>
                    <p>We may use Google Analytics to collect and process data, including when you use third party websites or apps. To find out more see <a href="http://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer">How Google uses data</a>.</p>
                </section>

                <section id="section4">
                    <h2>4. Use of Your Personal Information</h2>
                    <ul>
                        <li>to provide services or information to you;</li>
                        <li>for record keeping and administrative purposes;</li>
                        <li>to provide information about you to our contractors, employees, consultants, agents or other third parties for the purpose of providing goods or services to you;</li>
                        <li>to improve and optimise our service offering and customer experience;</li>
                        <li>to comply with our legal obligations, resolve disputes or enforce our agreements with third parties;</li>
                        <li>to send you marketing and promotional messages and other information that may be of interest to you and for the purpose of direct marketing (in accordance with the Spam Act);</li>
                        <li>to send you administrative messages, reminders, notices, updates, security alerts, and other information requested by you; and</li>
                        <li>to consider an application of employment from you.</li>
                    </ul>
                    <p>We may disclose your personal information to cloud-providers, contractors and other third parties located inside or outside of Australia. If we do so, we will take reasonable steps to ensure that any overseas recipient deals with such personal information in a manner consistent with how we deal with it.</p>
                </section>

                <section id="section5">
                    <h2>5. Security</h2>
                    <p>We take reasonable steps to ensure your personal information is secure and protected from misuse or unauthorised access. Our information technology systems are password protected, and we use a range of administrative and technical measures to protect these systems. However, we cannot guarantee the security of your personal information.</p>
                </section>

                <section id="section6">
                    <h2>6. Links</h2>
                    <p>Our website may contain links to other websites. Those links are provided for convenience and may not remain current or be maintained. We are not responsible for the privacy practices of those linked websites and we suggest you review the privacy policies of those websites before using them.</p>
                </section>

                <section id="section7">
                    <h2>7. Requesting Access or Correcting Your Personal Information</h2>
                    <p>If you wish to request access to the personal information we hold about you, please contact us using the contact details set out below including your name and contact details. We may need to verify your identity before providing you with your personal information. In some cases, we may be unable to provide you with access to all your personal information and where this occurs, we will explain why. We will deal with all requests for access to personal information within a reasonable timeframe.</p>
                    <p>If you think that any personal information we hold about you is inaccurate, please contact us using the contact details set out below and we will take reasonable steps to ensure that it is corrected.</p>
                </section>

                <section id="section8">
                    <h2>8. Complaints</h2>
                    <p>If you wish to complain about how we handle your personal information held by us, please contact us using the details set out below including your name and contact details. We will investigate your complaint promptly and respond to you within a reasonable timeframe.</p>
                </section>

                <section id="section9">
                    <h2>9. Contact Us</h2>
                    <p>For further information about our privacy policy or practices, or to access or correct your personal information, or make a complaint, please contact us using the details set out below:</p>
                    <p>Email: <a href="mailto:info@streamwisedi.com">info@streamwisedi.com</a></p>
                </section>

                <section id="section10">
                    <h2>PDF Download</h2>
                    <p>You can also <a href="\files\Privacy Policy Streamwisedi Pty Ltd.pdf" target="_blank" rel="noopener noreferrer">download the PDF version of this Privacy Policy</a>.</p>
                </section>
            </div>
        </div>
    );
};
